import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckOutlined as CheckIcon,
  CloseOutlined as CancelIcon,
  GestureOutlined as DrawIcon,
  LocationCityOutlined as CityIcon,
  Map as StreetsIcon,
  SatelliteAltOutlined as SatelliteIcon,
  UndoOutlined as UndoIcon,
} from '@mui/icons-material';
import type { FeatureCollection, LineString, Point, Polygon } from 'geojson';
import type { MapLayerMouseEvent } from 'react-map-gl/maplibre';
import Map, { Layer, NavigationControl, Source } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { APP_SHAPE } from '../../constants/shape';
import { mapStyleFor, type MapMode } from '../../data/mapStyles';
import type { Zone } from '../../types/zone.type';

const RIYADH_VIEW = { longitude: 46.6753, latitude: 24.7136, zoom: 11 };
type CityLayerDepth = 'regions' | 'governorates';
type InteractionMode = 'browse' | 'select-city' | 'draw';

interface ZonesMapProps {
  zones: Zone[];
  selectedZoneId: string | null;
  onSelectZone: (zoneId: string) => void;
  onBoundaryDrafted: (boundary: number[][]) => void;
  height?: number | string;
}

export default function ZonesMap({ zones, selectedZoneId, onSelectZone, onBoundaryDrafted, height = 'calc(100vh - 260px)' }: ZonesMapProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [mapMode, setMapMode] = useState<MapMode>('streets');
  const [mode, setMode] = useState<InteractionMode>('browse');
  const [cityDepth, setCityDepth] = useState<CityLayerDepth>('regions');
  const [governoratesGeojson, setGovernoratesGeojson] = useState<FeatureCollection<Polygon> | null>(null);
  const [draftPoints, setDraftPoints] = useState<[number, number][]>([]);

  useEffect(() => {
    if (cityDepth !== 'governorates' || governoratesGeojson) return;
    let cancelled = false;
    fetch('/data/saudi-governorates.geojson')
      .then((response) => response.json())
      .then((geojson: FeatureCollection<Polygon>) => { if (!cancelled) setGovernoratesGeojson(geojson); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cityDepth, governoratesGeojson]);

  const savedZonesData = useMemo((): FeatureCollection<Polygon> => ({
    type: 'FeatureCollection',
    features: zones.map((zone) => ({
      type: 'Feature',
      properties: { id: zone.id, color: zone.color, selected: zone.id === selectedZoneId },
      geometry: { type: 'Polygon', coordinates: [zone.boundary] },
    })),
  }), [zones, selectedZoneId]);

  const draftLineData = useMemo((): FeatureCollection<LineString> => {
    if (draftPoints.length < 2) return { type: 'FeatureCollection', features: [] };
    return { type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: draftPoints } }] };
  }, [draftPoints]);

  const draftPointsData = useMemo((): FeatureCollection<Point> => ({
    type: 'FeatureCollection',
    features: draftPoints.map((coords) => ({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: coords } })),
  }), [draftPoints]);

  const startDrawing = () => { setDraftPoints([]); setMode('draw'); };
  const startSelectCity = () => { setDraftPoints([]); setMode('select-city'); };
  const cancelInteraction = () => { setDraftPoints([]); setMode('browse'); };
  const undoLastPoint = () => setDraftPoints((prev) => prev.slice(0, -1));

  const finishShape = () => {
    if (draftPoints.length < 3) return;
    const ring = [...draftPoints, draftPoints[0]];
    setDraftPoints([]);
    setMode('browse');
    onBoundaryDrafted(ring);
  };

  const onMapClick = (event: MapLayerMouseEvent) => {
    if (mode === 'draw') {
      const { lng, lat } = event.lngLat;
      setDraftPoints((prev) => [...prev, [lng, lat]]);
      return;
    }
    const layerIds = mode === 'select-city' ? [`city-fill-${cityDepth}`] : ['saved-zones-fill'];
    const feats = event.target.queryRenderedFeatures(event.point, { layers: layerIds });
    if (!feats.length) return;
    if (mode === 'select-city') {
      const coordinates = (feats[0].geometry as Polygon).coordinates;
      setMode('browse');
      onBoundaryDrafted(coordinates[0]);
      return;
    }
    const zoneId = String(feats[0].properties?.id ?? '');
    if (zoneId) onSelectZone(zoneId);
  };

  const cityLayerSource = cityDepth === 'regions' ? '/data/saudi-regions.geojson' : governoratesGeojson;

  return (
    <Box sx={{ position: 'relative', height, minHeight: 420, borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden' }}>
      <Map
        initialViewState={RIYADH_VIEW}
        minZoom={3}
        maxZoom={18}
        mapStyle={mapStyleFor(mapMode)}
        style={{ width: '100%', height: '100%' }}
        attributionControl={{ compact: true }}
        onClick={onMapClick}
        interactiveLayerIds={mode === 'select-city' ? [`city-fill-${cityDepth}`] : ['saved-zones-fill']}
        cursor={mode === 'draw' ? 'crosshair' : mode === 'select-city' ? 'pointer' : undefined}
      >
        <NavigationControl position="top-right" showCompass={false} />

        {mode === 'select-city' && cityLayerSource && (
          <Source id="city-layer" type="geojson" data={cityLayerSource as string | FeatureCollection<Polygon>}>
            <Layer id={`city-fill-${cityDepth}`} type="fill" paint={{ 'fill-color': '#F4841A', 'fill-opacity': 0.15 }} />
            <Layer id="city-line" type="line" paint={{ 'line-color': '#F4841A', 'line-width': 1.2 }} />
          </Source>
        )}

        <Source id="saved-zones" type="geojson" data={savedZonesData}>
          <Layer
            id="saved-zones-fill"
            type="fill"
            paint={{ 'fill-color': ['get', 'color'], 'fill-opacity': ['case', ['get', 'selected'], 0.45, 0.22] }}
          />
          <Layer
            id="saved-zones-line"
            type="line"
            paint={{ 'line-color': ['get', 'color'], 'line-width': ['case', ['get', 'selected'], 3, 1.5] }}
          />
        </Source>

        <Source id="draft-line" type="geojson" data={draftLineData}>
          <Layer id="draft-line-layer" type="line" paint={{ 'line-color': '#ca8a04', 'line-width': 2, 'line-dasharray': [2, 1.5] }} />
        </Source>
        <Source id="draft-points" type="geojson" data={draftPointsData}>
          <Layer id="draft-points-layer" type="circle" paint={{ 'circle-radius': 5, 'circle-color': '#ca8a04', 'circle-stroke-width': 2, 'circle-stroke-color': '#fff' }} />
        </Source>
      </Map>

      <ToggleButtonGroup
        size="small"
        value={mapMode}
        exclusive
        onChange={(_, next: MapMode | null) => next && setMapMode(next)}
        sx={{ position: 'absolute', top: 12, insetInlineStart: 12, zIndex: 5, bgcolor: alpha(theme.palette.background.paper, 0.94), borderRadius: `${APP_SHAPE.radiusSm}px` }}
      >
        <ToggleButton value="streets" aria-label={t('zones.map.streets')}><StreetsIcon fontSize="small" /></ToggleButton>
        <ToggleButton value="satellite" aria-label={t('zones.map.satellite')}><SatelliteIcon fontSize="small" /></ToggleButton>
      </ToggleButtonGroup>

      {mode === 'browse' && (
        <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 12, insetInlineEnd: 52, zIndex: 5 }}>
          <Button size="small" variant="contained" startIcon={<CityIcon />} onClick={startSelectCity} sx={{ bgcolor: 'background.paper', color: 'text.primary', '&:hover': { bgcolor: 'background.paper' } }}>
            {t('zones.map.selectCity')}
          </Button>
          <Button size="small" variant="contained" startIcon={<DrawIcon />} onClick={startDrawing} sx={{ bgcolor: 'background.paper', color: 'text.primary', '&:hover': { bgcolor: 'background.paper' } }}>
            {t('zones.map.drawGeofence')}
          </Button>
        </Stack>
      )}

      {mode === 'select-city' && (
        <Stack spacing={1} sx={{ position: 'absolute', top: 12, insetInlineEnd: 52, zIndex: 5, alignItems: 'flex-end' }}>
          <ToggleButtonGroup size="small" value={cityDepth} exclusive onChange={(_, next: CityLayerDepth | null) => next && setCityDepth(next)} sx={{ bgcolor: alpha(theme.palette.background.paper, 0.94) }}>
            <ToggleButton value="regions">{t('zones.map.regions')}</ToggleButton>
            <ToggleButton value="governorates">{t('zones.map.governorates')}</ToggleButton>
          </ToggleButtonGroup>
          <Alert severity="info" icon={false} sx={{ py: 0.25 }}>{t('zones.map.selectCityHint')}</Alert>
          <Button size="small" color="error" startIcon={<CancelIcon />} onClick={cancelInteraction} variant="contained">{t('zones.map.cancel')}</Button>
        </Stack>
      )}

      {mode === 'draw' && (
        <Stack spacing={1} sx={{ position: 'absolute', top: 12, insetInlineEnd: 52, zIndex: 5, alignItems: 'flex-end' }}>
          <Alert severity="info" icon={false} sx={{ py: 0.25 }}>{t('zones.map.drawHint')}</Alert>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" startIcon={<CheckIcon />} onClick={finishShape} disabled={draftPoints.length < 3}>
              {t('zones.map.finishShape', { count: draftPoints.length })}
            </Button>
            <Button size="small" variant="contained" startIcon={<UndoIcon />} onClick={undoLastPoint} disabled={draftPoints.length === 0}>{t('zones.map.undo')}</Button>
            <Button size="small" color="error" variant="contained" startIcon={<CancelIcon />} onClick={cancelInteraction}>{t('zones.map.cancel')}</Button>
          </Stack>
        </Stack>
      )}

      {zones.length > 0 && mode === 'browse' && (
        <Chip
          label={t('zones.map.zoneCount', { count: zones.length })}
          sx={{ position: 'absolute', bottom: 12, insetInlineStart: 12, zIndex: 5, bgcolor: alpha(theme.palette.background.paper, 0.94) }}
        />
      )}
      {zones.length === 0 && mode === 'browse' && (
        <Typography variant="caption" sx={{ position: 'absolute', bottom: 12, insetInlineStart: 12, zIndex: 5, bgcolor: alpha(theme.palette.background.paper, 0.94), px: 1.5, py: 0.5, borderRadius: `${APP_SHAPE.radiusSm}px` }}>
          {t('zones.map.noZones')}
        </Typography>
      )}
    </Box>
  );
}
